import {
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import type { AppError } from '../../api/contract';
import { useAuth } from '../../auth/AuthProvider';
import type { BackofficePrincipal } from '../../auth/types';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { countActiveFilterValues } from '../../shared/filter/activeFilterCount';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { reportKinds } from '../reports/ReportPage';
import { apiReportKindLabel, reportKindLabel } from '../reports/labels';
import type { ReportKind } from '../reports/types';
import { reportExportsRepositoryToken } from './repository';
import type {
  ReportExport,
  ReportExportListFilters,
  ReportExportListQuery,
  ReportExportStatus,
} from './types';
import { defaultReportExportListQuery } from './types';

const statuses: readonly ReportExportStatus[] = [
  'QUEUED',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'EXPIRED',
];
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
const timestampPattern =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/u;

export function ReportExportsPage({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(reportExportsRepositoryToken);
  const auth = useAuth();
  const [params, setParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const principal = auth.status === 'authenticated' ? auth.principal : null;
  const [query, setQuery] = useState<ReportExportListQuery>(() => initialQuery(params, principal));
  const scopedDefaultFilters = normalizeFilters(defaultReportExportListQuery.filters, principal);
  const [draft, setDraft] = useState(query.filters);
  const [history, setHistory] = useState<readonly string[]>(['']);
  const [page, setPage] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const resource = useResource(
    (signal) => repository.list(query, signal),
    [query, refresh, repository],
    (result) => result.items.length === 0 && query.cursor === '',
  );
  const columns = useMemo<readonly GridColDef<ReportExport>[]>(
    () => [
      {
        field: 'reportType',
        headerName: t('reports.type'),
        minWidth: 220,
        flex: 1,
        valueFormatter: (value: string) => apiReportKindLabel(value, t),
      },
      {
        field: 'status',
        headerName: t('reports.status'),
        minWidth: 130,
        renderCell: ({ value }) => <Chip label={value} size="small" />,
      },
      {
        field: 'createdAt',
        headerName: t('reportExports.createdAt'),
        minWidth: 190,
        flex: 1,
        renderCell: ({ row }) => formatTimestamp(row.createdAt, row.timezone, language).display,
      },
      {
        field: 'expiresAt',
        headerName: t('reportExports.expiresAt'),
        minWidth: 190,
        flex: 1,
        renderCell: ({ row }) => formatTimestamp(row.expiresAt, row.timezone, language).display,
      },
      { field: 'recordCount', headerName: t('reportExports.recordCount'), minWidth: 110 },
      {
        field: 'actions',
        headerName: t('common.actions'),
        sortable: false,
        minWidth: 100,
        renderCell: ({ row }) => (
          <Button
            component={Link}
            size="small"
            to={`${paths.reportExports}/${encodeURIComponent(row.id)}`}
          >
            {t('actions.view')}
          </Button>
        ),
      },
    ],
    [language, t],
  );
  const apply = (event: FormEvent) => {
    event.preventDefault();
    const filters = normalizeFilters(draft, principal);
    setParams(new URLSearchParams(Object.entries(filters).filter(([, value]) => value !== '')), {
      replace: true,
    });
    setQuery({ ...query, cursor: '', filters });
    setHistory(['']);
    setPage(0);
  };
  const clear = () => {
    const filters = scopedDefaultFilters;
    setDraft(filters);
    setParams(new URLSearchParams(Object.entries(filters).filter(([, value]) => value !== '')), {
      replace: true,
    });
    setQuery({ ...query, cursor: '', filters });
    setHistory(['']);
    setPage(0);
  };
  const next = () => {
    const cursor = resource.data?.nextCursor;
    if (cursor === null || cursor === undefined) return;
    const index = page + 1;
    setHistory((current) => [...current.slice(0, index), cursor]);
    setPage(index);
    setQuery((current) => ({ ...current, cursor }));
  };
  const previous = () => {
    if (page === 0) return;
    const index = page - 1;
    setPage(index);
    setQuery((current) => ({ ...current, cursor: history[index] ?? '' }));
  };
  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={
          <Stack direction="row" spacing={1}>
            <DataSourceBadge source={dataSource} />
            <Button component={Link} to={paths.reports}>
              {t('nav.reports')}
            </Button>
          </Stack>
        }
        subtitle={t('reportExports.subtitle')}
        title={t('reportExports.title')}
      />
      <ListFilterPanel
        activeSecondaryFilterCount={countActiveFilterValues(
          {
            partnerId: query.filters.partnerId,
            agentId: query.filters.agentId,
            to: query.filters.to,
          },
          {
            partnerId: scopedDefaultFilters.partnerId,
            agentId: scopedDefaultFilters.agentId,
            to: scopedDefaultFilters.to,
          },
        )}
        ariaLabel={t('filterPanel.label', { page: t('nav.reportExports') })}
        onClear={clear}
        onSubmit={apply}
        primaryFields={
          <>
            <TextField
              label={t('reports.type')}
              onChange={(event) =>
                setDraft({ ...draft, reportType: event.target.value as ReportKind | '' })
              }
              select
              size="small"
              value={draft.reportType}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {reportKinds.map((kind) => (
                <MenuItem key={kind} value={kind}>
                  {reportKindLabel(kind, t)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('reports.status')}
              onChange={(event) =>
                setDraft({ ...draft, status: event.target.value as ReportExportStatus | '' })
              }
              select
              size="small"
              value={draft.status}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {statuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
            {(['requestedBy', 'from'] as const).map((field) => (
              <TextField
                disabled={isLockedFilter(field, principal)}
                key={field}
                label={t(`reportExports.filters.${field}`)}
                onChange={(event) => setDraft({ ...draft, [field]: event.target.value })}
                size="small"
                value={draft[field]}
              />
            ))}
          </>
        }
        secondaryFields={
          <>
            {(['partnerId', 'agentId', 'to'] as const).map((field) => (
              <TextField
                disabled={isLockedFilter(field, principal)}
                key={field}
                label={t(`reportExports.filters.${field}`)}
                onChange={(event) => setDraft({ ...draft, [field]: event.target.value })}
                size="small"
                value={draft[field]}
              />
            ))}
          </>
        }
      />
      <PageState
        emptyMessage={t('reportExports.empty')}
        error={safeError(resource.error, t('errors.generic'))}
        onRetry={() => setRefresh((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={1}>
            {mobile ? (
              <Stack
                aria-label={t('reportExports.title')}
                component="ul"
                spacing={1}
                sx={{ listStyle: 'none', m: 0, p: 0 }}
              >
                {resource.data.items.map((item) => (
                  <Paper component="li" key={item.id} sx={{ p: 2 }} variant="outlined">
                    <Stack spacing={0.5}>
                      <Chip label={item.status} size="small" sx={{ alignSelf: 'flex-start' }} />
                      <Typography sx={{ fontWeight: 700 }}>
                        {apiReportKindLabel(item.reportType, t)}
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        {formatTimestamp(item.createdAt, item.timezone, language).display}
                      </Typography>
                      <Button
                        component={Link}
                        size="small"
                        sx={{ alignSelf: 'flex-start' }}
                        to={`${paths.reportExports}/${encodeURIComponent(item.id)}`}
                      >
                        {t('actions.view')}
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Box
                aria-label={t('reportExports.title')}
                role="region"
                sx={{ overflowX: 'auto' }}
                tabIndex={0}
              >
                <Box sx={{ height: 430, minWidth: 1000 }}>
                  <DataGrid
                    columns={columns}
                    disableRowSelectionOnClick
                    hideFooter
                    rows={resource.data.items}
                    sortingMode="server"
                  />
                </Box>
              </Box>
            )}
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
              <Button disabled={page === 0} onClick={previous}>
                {t('reportExports.pagination.previous')}
              </Button>
              <Button disabled={resource.data.nextCursor === null} onClick={next}>
                {t('reportExports.pagination.next')}
              </Button>
            </Stack>
            {resource.data.requestId === null ? null : (
              <Typography variant="caption">
                {t('reports.requestId', { id: resource.data.requestId })}
              </Typography>
            )}
          </Stack>
        )}
      </PageState>
    </Box>
  );
}

function initialQuery(
  params: URLSearchParams,
  principal: BackofficePrincipal | null,
): ReportExportListQuery {
  const raw: ReportExportListFilters = {
    reportType: reportKinds.includes(params.get('reportType') as ReportKind)
      ? (params.get('reportType') as ReportKind)
      : '',
    status: statuses.includes(params.get('status') as ReportExportStatus)
      ? (params.get('status') as ReportExportStatus)
      : '',
    requestedBy: params.get('requestedBy') ?? '',
    partnerId: params.get('partnerId') ?? '',
    agentId: params.get('agentId') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
  };
  return { cursor: '', pageSize: 50, filters: normalizeFilters(raw, principal) };
}
function normalizeFilters(
  value: ReportExportListFilters,
  principal: BackofficePrincipal | null,
): ReportExportListFilters {
  const normalized = {
    ...value,
    requestedBy: uuidPattern.test(value.requestedBy.trim()) ? value.requestedBy.trim() : '',
    partnerId: uuidPattern.test(value.partnerId.trim()) ? value.partnerId.trim() : '',
    agentId: uuidPattern.test(value.agentId.trim()) ? value.agentId.trim() : '',
    from: validTimestamp(value.from),
    to: validTimestamp(value.to),
  };
  if (principal === null) return normalized;
  return {
    ...normalized,
    partnerId: principal.scope.partnerId ?? normalized.partnerId,
    agentId: principal.scope.agentId ?? normalized.agentId,
    requestedBy: principal.role === 'SUPER_ADMIN' ? normalized.requestedBy : principal.actor.id,
  };
}
function validTimestamp(value: string): string {
  const trimmed = value.trim();
  return timestampPattern.test(trimmed) && !Number.isNaN(Date.parse(trimmed)) ? trimmed : '';
}
function safeError(error: unknown, fallback: string): AppError | null {
  if (error === null) return null;
  const requestId =
    typeof error === 'object' &&
    error !== null &&
    'requestId' in error &&
    typeof error.requestId === 'string'
      ? error.requestId
      : null;
  return {
    name: 'AppError',
    message: fallback,
    status: 0,
    code: 'REPORT_EXPORT_ERROR',
    requestId,
    fieldErrors: {},
    retryAfterSeconds: null,
  } as AppError;
}

function isLockedFilter(
  field: keyof ReportExportListFilters,
  principal: BackofficePrincipal | null,
): boolean {
  if (principal === null) return false;
  if (field === 'partnerId') return principal.scope.partnerId !== null;
  if (field === 'agentId') return principal.scope.agentId !== null;
  if (field === 'requestedBy') return principal.role !== 'SUPER_ADMIN';
  return false;
}
